import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function CampaignDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [assets, setAssets] = useState([]);
  const [metricForm, setMetricForm] = useState({ date: '', impressions: '', clicks: '', conversions: '', spend: '', revenue: '' });
  const [showMetricForm, setShowMetricForm] = useState(false);

  useEffect(() => {
    api(`/marketing/campaigns/${id}`).then(r => r.ok ? r.json() : null).then(setCampaign).catch(() => {});
    api(`/marketing/campaigns/${id}/metrics`).then(r => r.ok ? r.json() : []).then(setMetrics).catch(() => {});
    api(`/marketing/campaigns/${id}/assets`).then(r => r.ok ? r.json() : []).then(setAssets).catch(() => {});
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this campaign?')) return;
    await api(`/marketing/campaigns/${id}`, {method: 'DELETE'});
    navigate('/marketing/campaigns');
  };

  const handleAddMetric = async (e) => {
    e.preventDefault();
    const body = {
      date: metricForm.date,
      impressions: metricForm.impressions ? parseInt(metricForm.impressions) : 0,
      clicks: metricForm.clicks ? parseInt(metricForm.clicks) : 0,
      conversions: metricForm.conversions ? parseInt(metricForm.conversions) : 0,
      spend: metricForm.spend ? parseFloat(metricForm.spend) : 0,
      revenue: metricForm.revenue ? parseFloat(metricForm.revenue) : 0,
    };
    const res = await api(`/marketing/campaigns/${id}/metrics`, {method: 'POST', body: JSON.stringify(body)});
    if (res.ok) {
      const m = await res.json();
      setMetrics([m, ...metrics]);
      setMetricForm({ date: '', impressions: '', clicks: '', conversions: '', spend: '', revenue: '' });
      setShowMetricForm(false);
    }
  };

  const handleDeleteMetric = async (metricId) => {
    await api(`/marketing/campaigns/${id}/metrics/${metricId}`, {method: 'DELETE'});
    setMetrics(metrics.filter(m => m.id !== metricId));
  };

  const handleUploadAsset = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    const res = await api(`/marketing/campaigns/${id}/assets`, {method: 'POST', body: formData});
    if (res.ok) {
      const a = await res.json();
      setAssets([a, ...assets]);
    }
    e.target.value = '';
  };

  const handleDownloadAsset = async (assetId) => {
    const res = await api(`/marketing/campaigns/${id}/assets/${assetId}`);
    if (res.ok) {
      const {url} = await res.json();
      window.open(url, '_blank');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    await api(`/marketing/campaigns/${id}/assets/${assetId}`, {method: 'DELETE'});
    setAssets(assets.filter(a => a.id !== assetId));
  };

  if (!campaign) return <Layout><p className="text-gray-500">Loading...</p></Layout>;

  const totalImpressions = metrics.reduce((s, m) => s + (m.impressions || 0), 0);
  const totalClicks = metrics.reduce((s, m) => s + (m.clicks || 0), 0);
  const totalConversions = metrics.reduce((s, m) => s + (m.conversions || 0), 0);
  const totalSpend = metrics.reduce((s, m) => s + (m.spend || 0), 0);
  const totalRevenue = metrics.reduce((s, m) => s + (m.revenue || 0), 0);
  const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
  const cpc = totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : '0.00';
  const roas = totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : '0.00';

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{campaign.name}</h2>
        <div className="flex gap-3">
          <Link to={`/marketing/campaigns/${id}/edit`} className="bg-fuchsia-600 text-white px-4 py-2 rounded-lg hover:bg-fuchsia-700">Edit</Link>
          <button onClick={handleDelete} className="border border-red-300 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50">Delete</button>
        </div>
      </div>

      {/* Campaign Info */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><p className="text-sm text-gray-500">Platform</p><p className="font-medium">{campaign.platform}</p></div>
          <div><p className="text-sm text-gray-500">Status</p><p className="font-medium">{campaign.status}</p></div>
          <div><p className="text-sm text-gray-500">Budget</p><p className="font-medium">{campaign.budget ? `$${campaign.budget.toLocaleString()}` : '—'}</p></div>
          <div><p className="text-sm text-gray-500">Dates</p><p className="font-medium">{campaign.startDate || '—'} → {campaign.endDate || '—'}</p></div>
        </div>
        {campaign.externalUrl && (
          <div className="mt-4">
            <a href={campaign.externalUrl} target="_blank" rel="noopener noreferrer" className="text-fuchsia-600 hover:underline break-all">{campaign.externalUrl}</a>
          </div>
        )}
      </div>

      {/* Analytics Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Impressions</p><p className="text-lg font-bold">{totalImpressions.toLocaleString()}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Clicks</p><p className="text-lg font-bold">{totalClicks.toLocaleString()}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Conversions</p><p className="text-lg font-bold">{totalConversions.toLocaleString()}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Spend</p><p className="text-lg font-bold">${totalSpend.toLocaleString()}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Revenue</p><p className="text-lg font-bold">${totalRevenue.toLocaleString()}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">CTR</p><p className="text-lg font-bold">{ctr}%</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">ROAS</p><p className="text-lg font-bold">{roas}x</p></div>
      </div>

      {/* Metrics Table */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Metrics</h3>
          <button onClick={() => setShowMetricForm(!showMetricForm)} className="text-sm bg-fuchsia-600 text-white px-3 py-1 rounded hover:bg-fuchsia-700">
            {showMetricForm ? 'Cancel' : 'Add Entry'}
          </button>
        </div>
        {showMetricForm && (
          <form onSubmit={handleAddMetric} className="p-4 border-b border-gray-200 grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
            <div><label className="text-xs text-gray-500">Date *</label><input type="date" required value={metricForm.date} onChange={e => setMetricForm({...metricForm, date: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" /></div>
            <div><label className="text-xs text-gray-500">Impressions</label><input type="number" value={metricForm.impressions} onChange={e => setMetricForm({...metricForm, impressions: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" /></div>
            <div><label className="text-xs text-gray-500">Clicks</label><input type="number" value={metricForm.clicks} onChange={e => setMetricForm({...metricForm, clicks: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" /></div>
            <div><label className="text-xs text-gray-500">Conversions</label><input type="number" value={metricForm.conversions} onChange={e => setMetricForm({...metricForm, conversions: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" /></div>
            <div><label className="text-xs text-gray-500">Spend</label><input type="number" step="0.01" value={metricForm.spend} onChange={e => setMetricForm({...metricForm, spend: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" /></div>
            <div><label className="text-xs text-gray-500">Revenue</label><input type="number" step="0.01" value={metricForm.revenue} onChange={e => setMetricForm({...metricForm, revenue: e.target.value})} className="w-full border rounded px-2 py-1 text-sm" /></div>
            <button type="submit" className="bg-fuchsia-600 text-white px-3 py-1 rounded text-sm hover:bg-fuchsia-700">Save</button>
          </form>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Impressions</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Clicks</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Conversions</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Spend</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Revenue</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {metrics.map(m => (
                <tr key={m.id}>
                  <td className="px-4 py-2">{m.date}</td>
                  <td className="px-4 py-2 text-right">{(m.impressions || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{(m.clicks || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{(m.conversions || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">${(m.spend || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">${(m.revenue || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => handleDeleteMetric(m.id)} className="text-red-500 hover:text-red-700 text-xs">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {metrics.length === 0 && <p className="p-4 text-center text-gray-500 text-sm">No metrics recorded yet.</p>}
        </div>
      </div>

      {/* Assets */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Assets</h3>
          <label className="text-sm bg-fuchsia-600 text-white px-3 py-1 rounded hover:bg-fuchsia-700 cursor-pointer">
            Upload
            <input type="file" className="hidden" onChange={handleUploadAsset} />
          </label>
        </div>
        <div className="divide-y divide-gray-200">
          {assets.map(a => (
            <div key={a.id} className="flex items-center justify-between p-4">
              <div>
                <p className="font-medium text-gray-900">{a.fileName}</p>
                <p className="text-xs text-gray-500">{a.contentType} · {a.size ? `${(a.size / 1024).toFixed(1)} KB` : ''}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleDownloadAsset(a.id)} className="text-fuchsia-600 hover:text-fuchsia-800 text-sm">Download</button>
                <button onClick={() => handleDeleteAsset(a.id)} className="text-red-500 hover:text-red-700 text-sm">Delete</button>
              </div>
            </div>
          ))}
          {assets.length === 0 && <p className="p-4 text-center text-gray-500 text-sm">No assets uploaded yet.</p>}
        </div>
      </div>
    </Layout>
  );
}
